import { useState } from 'react';
import { staffApi } from '../api';
import {
  IconCalendar, IconChevronLeft, IconChevronRight, IconEdit, IconGrip,
} from '../components/icons';
import {
  Empty, ErrorNote, Modal, Spinner, TH_LOCALE, dateTh, dayLabel, shiftDate, shopInstantIso, timeOf, todayIso, useLoader,
} from '../components/common';

/**
 * เข้างาน — the roster board. Several day columns side by side; the queue order
 * in the first column is what the calendar board uses for lane order, so a drag
 * here changes who gets offered the next walk-in.
 *
 * There is no "add a therapist" step: a therapist is hired once for the shop and
 * appears on every branch's board automatically, so the only decision here is
 * ทำงาน / เลิกงาน.
 */
export default function StaffRoster() {
  const [startDate, setStartDate] = useState(todayIso());
  const [drag, setDrag] = useState(null);

  const roster = useLoader(() => staffApi.roster(startDate, 3), [startDate]);
  /** Still taking guests, in queue order. */
  const queued = (column) => column.shifts.filter((s) => s.status !== 'off_duty');
  /** Clocked out — shown below, out of the queue. */
  const finished = (column) => column.shifts.filter((s) => s.status === 'off_duty');

  const toggle = async (shift) => {
    await staffApi.setStatus(shift.id, shift.status === 'working' ? 'off_duty' : 'working');
    roster.reload();
  };

  /** Reorders within one column and persists the whole new queue. */
  const drop = async (column, targetShift) => {
    if (!drag || drag.date !== column.date || drag.shift.id === targetShift.id) return;

    const ids = column.shifts.map((s) => s.id);
    const from = ids.indexOf(drag.shift.id);
    const to = ids.indexOf(targetShift.id);
    ids.splice(to, 0, ids.splice(from, 1)[0]);

    setDrag(null);
    await staffApi.reorder(column.date, ids);
    roster.reload();
  };

  if (roster.loading) return <Spinner />;
  if (roster.error) return <ErrorNote error={roster.error} />;

  return (
    <>
      <div className="cal-toolbar">
        <button className="btn-today" onClick={() => setStartDate(todayIso())}>วันนี้</button>
        <button className="btn-step" onClick={() => setStartDate(shiftDate(startDate, -1))}>
          <IconChevronLeft size={18} />
        </button>
        <label className="date-box">
          <span>{dateTh(startDate)}</span>
          <IconCalendar size={17} />
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <button className="btn-step" onClick={() => setStartDate(shiftDate(startDate, 1))}>
          <IconChevronRight size={18} />
        </button>
      </div>

      <div className="roster-cols">
        {roster.data.columns.map((column) => (
          <div className="roster-col" key={column.date}>
            <h3>
              {dayLabel(column.date, TH_LOCALE, {
                day: 'numeric', month: 'long', year: '2-digit',
              })}
            </h3>

            {column.shifts.length === 0 && <Empty icon="staff" message="ยังไม่มีหมอนวดเข้างาน" />}

            {queued(column).map((s) => (
              <ShiftRow
                key={s.id}
                shift={s}
                queued
                live={column.date === todayIso()}
                dragging={drag?.shift.id === s.id}
                onDragStart={() => setDrag({ date: column.date, shift: s })}
                onDragEnd={() => setDrag(null)}
                onDrop={() => drop(column, s)}
                onToggle={() => toggle(s)}
              />
            ))}

            {/* Anyone who has clocked out drops out of the queue: no number,
                no drag handle — they are no longer offered the next walk-in. */}
            {finished(column).length > 0 && <div className="roster-split" />}
            {finished(column).map((s) => (
              <ShiftRow
                key={s.id}
                shift={s}
                live={column.date === todayIso()}
                onToggle={() => toggle(s)}
              />
            ))}
          </div>
        ))}
      </div>

    </>
  );
}

/**
 * One therapist on the roster.
 *
 * The status is a button, not a badge — it is the ทำงาน / เลิกงาน toggle, and
 * an outline reads as "press me" where a filled pill reads as a label.
 */
function ShiftRow({ shift, queued, live, dragging, onDragStart, onDragEnd, onDrop, onToggle }) {
  const working = shift.status === 'working';
  return (
    <div
      className={`roster-card${dragging ? ' dragging' : ''}${queued ? '' : ' finished'}`}
      draggable={!!queued}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <span className="grip"><IconGrip size={18} /></span>
      {queued && live && <span className="lane-queue">#{shift.queueNo}</span>}
      {/* Blank when there is no photograph — the real board shows a plain disc,
          not initials. */}
      <div className="lane-avatar">
        {shift.photoUrl ? <img src={shift.photoUrl} alt="" /> : null}
      </div>
      <span className="name">
        {shift.nicknameEn || shift.nicknameTh}
        {shift.nicknameEn && shift.nicknameTh ? ` (${shift.nicknameTh})` : ''}
      </span>

      {/* A future day is a plan, not a shift: nothing to clock, nothing to edit. */}
      {live && (
        <>
          {/* The pill states what the shift IS. Changing it lives behind the
              pencil, which is where the original puts it — measured: their menu
              is 81x42, one item, 16px/500, green when the action is เริ่มงาน. */}
          <span className={`shift-status${working ? ' on' : ''}`}>
            {working ? 'ทำงาน' : 'เลิกงาน'}
          </span>
          <ShiftMenu working={working} onToggle={onToggle} />
        </>
      )}
    </div>
  );
}

/** The pencil's menu: the status change first, then the shift's own details. */
function ShiftMenu({ working, onToggle }) {
  const [open, setOpen] = useState(false);

  return (
    <span className="shift-menu">
      <button
        className="icon-square pen"
        onClick={() => setOpen((o) => !o)}
        title="เปลี่ยนสถานะ"
      >
        <IconEdit size={17} />
      </button>
      {open && (
        <>
          <div className="shift-menu-scrim" onMouseDown={() => setOpen(false)} />
          <div className="shift-menu-pop">
            <button
              className={working ? 'stop' : 'start'}
              onClick={() => { setOpen(false); onToggle(); }}
            >
              {working ? 'เลิกงาน' : 'เริ่มงาน'}
            </button>
          </div>
        </>
      )}
    </span>
  );
}

const hhmmOf = (iso) => (iso ? timeOf(iso) : '');

/**
 * แก้ไขการเข้างาน — clock times, the daily guarantee and a note.
 *
 * Not reachable from this page. Their pencil is a status menu and nothing else
 * (measured: 81x42, one item), so correcting a clock time is not something the
 * roster does on the original — it belongs to เข้างาน, which we have not built
 * yet. Exported so that page can mount it rather than have it rewritten.
 */
export function ShiftEditor({ shift, onClose, onSaved }) {
  const [form, setForm] = useState({
    clockIn: hhmmOf(shift.clockInAt),
    clockOut: hhmmOf(shift.clockOutAt),
    dailyGuarantee: shift.dailyGuarantee ?? '',
    note: shift.note || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await staffApi.update(shift.id, {
        // A blank field clears the stamp rather than leaving yesterday's value.
        clockInAt: form.clockIn ? shopInstantIso(shift.workDate, form.clockIn) : null,
        clockOutAt: form.clockOut ? shopInstantIso(shift.workDate, form.clockOut) : null,
        dailyGuarantee: form.dailyGuarantee === '' ? null : Number(form.dailyGuarantee),
        note: form.note,
      });
      onSaved();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  const name = `${shift.nicknameEn || shift.nicknameTh}${
    shift.nicknameEn && shift.nicknameTh ? ` (${shift.nicknameTh})` : ''}`;

  return (
    <Modal title={`แก้ไขการเข้างาน — ${name}`} onClose={onClose}>
      <ErrorNote error={error} />

      <div className="grid-2">
        <label className="field">
          <span>เวลาเข้างาน</span>
          <input type="time" value={form.clockIn} onChange={set('clockIn')} />
        </label>
        <label className="field">
          <span>เวลาเลิกงาน</span>
          <input type="time" value={form.clockOut} onChange={set('clockOut')} />
        </label>
      </div>

      <label className="field">
        <span>ค่าประกันรายวัน (บาท)</span>
        <input
          type="number" min="0" step="1" placeholder="ไม่มี"
          value={form.dailyGuarantee} onChange={set('dailyGuarantee')}
        />
      </label>

      <label className="field">
        <span>หมายเหตุ</span>
        <textarea rows={2} value={form.note} onChange={set('note')} />
      </label>

      <div className="modal-actions">
        <div style={{ flex: 1 }} />
        <button className="btn" disabled={busy} onClick={onClose}>ยกเลิก</button>
        <button className="btn btn-solid" disabled={busy} onClick={save}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}
